/**
 * QUALITY SCORE ENGINE
 *
 * Evaluates overall plan quality (0–100) across five dimensions:
 *   1. Push / Pull balance
 *   2. Movement pattern coverage
 *   3. Fatigue management
 *   4. Volume distribution
 *   5. Progression logic
 *
 * Each dimension scores 0–100, then a weighted average produces the final score.
 */

import type {
  Week,
  MovementPattern,
  QualityMetrics,
  QualitySuggestion,
  TrainingLevel,
  ProgressionModel,
  FatigueMetrics,
  VolumeMetrics,
} from '../types';
import { MOVEMENT_PATTERN_LABELS, MUSCLE_GROUP_LABELS } from '../types';

// ── Dimension weights ───────────────────────
const WEIGHTS = {
  pushPullBalance: 0.25,
  movementCoverage: 0.25,
  fatigueManagement: 0.20,
  volumeDistribution: 0.20,
  progressionLogic: 0.10,
};

// ── 1. Push / Pull Balance ──────────────────

function scorePushPullBalance(week: Week): { score: number; suggestions: QualitySuggestion[] } {
  let pushSets = 0;
  let pullSets = 0;
  const suggestions: QualitySuggestion[] = [];

  for (const session of week.sessions) {
    for (const ex of session.exercises) {
      if (['horizontal_push', 'vertical_push'].includes(ex.movementPattern)) {
        pushSets += ex.sets;
      }
      if (['horizontal_pull', 'vertical_pull'].includes(ex.movementPattern)) {
        pullSets += ex.sets;
      }
    }
  }

  if (pushSets === 0 && pullSets === 0) return { score: 50, suggestions: [] };

  const total = pushSets + pullSets;
  const ratio = pushSets / Math.max(pullSets, 1);

  // Ideal ratio ≈ 1:1 to 1:1.2 (slightly more pull)
  let score: number;
  if (ratio >= 0.8 && ratio <= 1.2) {
    score = 100;
  } else if (ratio >= 0.6 && ratio <= 1.5) {
    score = 75;
  } else if (ratio >= 0.4 && ratio <= 2.0) {
    score = 50;
  } else {
    score = 25;
  }

  if (ratio > 1.3) {
    suggestions.push({
      category: 'balance',
      priority: ratio > 1.8 ? 'high' : 'medium',
      message: `Stosunek Push/Pull wynosi ${ratio.toFixed(1)}:1 - dodaj więcej objętości ciągnienia (wiosłowania, ściąganie) dla zdrowia barków.`,
    });
  } else if (ratio < 0.7) {
    suggestions.push({
      category: 'balance',
      priority: 'medium',
      message: `Objętość ciągnienia znacznie przekracza pchanie - rozważ zrównoważenie większą ilością ćwiczeń pchających.`,
    });
  }

  return { score, suggestions };
}

// ── 2. Movement Pattern Coverage ────────────

const ESSENTIAL_PATTERNS: MovementPattern[] = [
  'squat', 'hinge', 'horizontal_push', 'vertical_push', 'horizontal_pull', 'vertical_pull',
];
const BONUS_PATTERNS: MovementPattern[] = ['carry', 'core'];

function scoreMovementCoverage(week: Week): { score: number; suggestions: QualitySuggestion[] } {
  const suggestions: QualitySuggestion[] = [];
  const presentPatterns = new Set<MovementPattern>();

  for (const session of week.sessions) {
    for (const ex of session.exercises) {
      presentPatterns.add(ex.movementPattern);
    }
  }

  const essentialCovered = ESSENTIAL_PATTERNS.filter((p) => presentPatterns.has(p));
  const bonusCovered = BONUS_PATTERNS.filter((p) => presentPatterns.has(p));

  const essentialScore = (essentialCovered.length / ESSENTIAL_PATTERNS.length) * 85;
  const bonusScore = (bonusCovered.length / BONUS_PATTERNS.length) * 15;
  const score = Math.round(essentialScore + bonusScore);

  const missing = ESSENTIAL_PATTERNS.filter((p) => !presentPatterns.has(p));
  for (const p of missing) {
    suggestions.push({
      category: 'coverage',
      priority: 'high',
      message: `Brakuje wzorca ruchu: ${MOVEMENT_PATTERN_LABELS[p]}. Dodaj przynajmniej jedno ćwiczenie dla zrównoważonego rozwoju.`,
    });
  }

  if (!presentPatterns.has('core')) {
    suggestions.push({
      category: 'coverage',
      priority: 'medium',
      message: 'Brak bezpośredniego treningu core - rozważ dodanie deski, rollout lub Pallof press.',
    });
  }
  if (!presentPatterns.has('carry')) {
    suggestions.push({
      category: 'coverage',
      priority: 'low',
      message: "Brak obciążonych przenoszek - chód farmera buduje chwyt, stabilność core i kondycję.",
    });
  }

  return { score, suggestions };
}

// ── 3. Fatigue Management ───────────────────

function scoreFatigueManagement(fatigue: FatigueMetrics): { score: number; suggestions: QualitySuggestion[] } {
  const suggestions: QualitySuggestion[] = [];

  // Score inversely to fatigue level
  let score: number;
  if (fatigue.overallFatigue <= 40) score = 100;
  else if (fatigue.overallFatigue <= 55) score = 85;
  else if (fatigue.overallFatigue <= 70) score = 65;
  else if (fatigue.overallFatigue <= 85) score = 40;
  else score = 15;

  if (fatigue.warnings.filter((w) => w.severity === 'critical').length > 0) {
    score = Math.min(score, 30);
    suggestions.push({
      category: 'fatigue',
      priority: 'high',
      message: 'Obecne krytyczne ostrzeżenia o zmęczeniu - przejrzyj strukturę sesji pilnie.',
    });
  }

  if (fatigue.overallFatigue > 60) {
    suggestions.push({
      category: 'fatigue',
      priority: 'medium',
      message: 'Rozważ planowanie tygodnia odciążającego co 3–4 tygodnie, aby zarządzać skumulowanym zmęczeniem.',
    });
  }

  return { score, suggestions };
}

// ── 4. Volume Distribution ──────────────────

function scoreVolumeDistribution(volume: VolumeMetrics): { score: number; suggestions: QualitySuggestion[] } {
  const suggestions: QualitySuggestion[] = [];

  // For starter plans we evaluate distribution mainly on trainable groups with non-zero MEV.
  const relevantMuscles = volume.muscleVolumes.filter((mv) => mv.mev > 0);
  const statuses = relevantMuscles.map((mv) => mv.status);
  const optimalCount = statuses.filter((s) => s === 'optimal').length;
  const atMevCount = statuses.filter((s) => s === 'at_mev').length;
  const belowCount = statuses.filter((s) => s === 'below_mev').length;
  const overCount = statuses.filter((s) => s === 'exceeds_mrv').length;
  const total = Math.max(statuses.length, 1);

  // Score based on how many muscles are in good ranges
  const goodCount = optimalCount + atMevCount;
  let score = Math.round((goodCount / total) * 80 + (optimalCount / total) * 20);

  // Penalties for over/under
  score -= overCount * 8;
  score -= belowCount * 3;
  score = Math.max(0, Math.min(100, score));

  if (overCount > 0) {
    const overMuscles = relevantMuscles
      .filter((mv) => mv.status === 'exceeds_mrv')
      .map((mv) => MUSCLE_GROUP_LABELS[mv.muscle]);
    suggestions.push({
      category: 'volume',
      priority: 'high',
      message: `Nadmierna objętość dla: ${overMuscles.join(', ')}. Zmniejsz serie, aby pozostać w granicach MRV.`,
    });
  }

  if (belowCount > 3) {
    suggestions.push({
      category: 'volume',
      priority: 'medium',
      message: `${belowCount} kluczowych grup mięśniowych jest poniżej MEV - zwiększ ukierunkowaną objętość (2-4 serie/tydz.).`,
    });
  }

  // Compound ratio check
  if (volume.compoundRatio < 0.4) {
    suggestions.push({
      category: 'volume',
      priority: 'medium',
      message: 'Stosunek ćwiczeń złożonych jest niski. Ćwiczenia złożone są bardziej czasowo efektywne i stymulują więcej mięśni.',
    });
  }

  return { score, suggestions };
}

// ── 5. Progression Logic ────────────────────

function scoreProgressionLogic(
  progressionModel: ProgressionModel,
  trainingLevel: TrainingLevel,
  mesocycleLength: number
): { score: number; suggestions: QualitySuggestion[] } {
  const suggestions: QualitySuggestion[] = [];
  let score = 70; // base score

  // Level-appropriate progression
  if (trainingLevel === 'beginner' && progressionModel === 'linear') score += 20;
  else if (trainingLevel === 'beginner' && progressionModel !== 'linear') {
    score -= 10;
    suggestions.push({
      category: 'progression',
      priority: 'medium',
      message: 'Początkujący najbardziej korzystają z progresji liniowej - rozważ zmianę.',
    });
  }

  if (trainingLevel === 'intermediate' && ['undulating', 'linear'].includes(progressionModel)) {
    score += progressionModel === 'undulating' ? 18 : 12;
  }

  if (trainingLevel === 'advanced' && ['undulating', 'block', 'autoregulated'].includes(progressionModel)) {
    score += progressionModel === 'linear' ? 0 : 18;
  }

  if (['advanced', 'elite'].includes(trainingLevel) && progressionModel === 'linear') {
    score -= 15;
    suggestions.push({
      category: 'progression',
      priority: 'medium',
      message: 'Zaawansowani zazwyczaj potrzebują falowania lub periodyzacji blokowej dla dalszych postępów.',
    });
  }

  if (['advanced', 'elite'].includes(trainingLevel) && ['block', 'autoregulated'].includes(progressionModel)) {
    score += 15;
  }

  if (trainingLevel === 'elite' && progressionModel === 'autoregulated') {
    score += 5;
  }

  // Mesocycle length check
  if (mesocycleLength >= 4 && mesocycleLength <= 6) {
    score += 5;
  }
  if (mesocycleLength < 3) {
    score -= 10;
    suggestions.push({
      category: 'progression',
      priority: 'low',
      message: 'Mezocykl jest bardzo krótki (< 3 tygodnie). Większość adaptacji potrzebuje 3–6 tygodni, aby się rozwinąć.',
    });
  }
  if (mesocycleLength > 8) {
    score -= 5;
    suggestions.push({
      category: 'progression',
      priority: 'low',
      message: 'Bardzo długi mezocykl - rozważ podział na krótsze bloki z tygodniami odciążającymi.',
    });
  }

  return { score: Math.max(0, Math.min(100, score)), suggestions };
}

// ── Main quality calculator ─────────────────

export function calculateQualityMetrics(
  week: Week,
  volume: VolumeMetrics,
  fatigue: FatigueMetrics,
  progressionModel: ProgressionModel,
  trainingLevel: TrainingLevel,
  mesocycleLength: number
): QualityMetrics {
  const ppb = scorePushPullBalance(week);
  const mc = scoreMovementCoverage(week);
  const fm = scoreFatigueManagement(fatigue);
  const vd = scoreVolumeDistribution(volume);
  const pl = scoreProgressionLogic(progressionModel, trainingLevel, mesocycleLength);

  const breakdown = {
    pushPullBalance: ppb.score,
    movementCoverage: mc.score,
    fatigueManagement: fm.score,
    volumeDistribution: vd.score,
    progressionLogic: pl.score,
  };

  const overallScore = Math.round(
    breakdown.pushPullBalance * WEIGHTS.pushPullBalance +
    breakdown.movementCoverage * WEIGHTS.movementCoverage +
    breakdown.fatigueManagement * WEIGHTS.fatigueManagement +
    breakdown.volumeDistribution * WEIGHTS.volumeDistribution +
    breakdown.progressionLogic * WEIGHTS.progressionLogic
  );

  const suggestions = [
    ...ppb.suggestions,
    ...mc.suggestions,
    ...fm.suggestions,
    ...vd.suggestions,
    ...pl.suggestions,
  ].sort((a, b) => {
    const prio = { high: 0, medium: 1, low: 2 };
    return prio[a.priority] - prio[b.priority];
  });

  return { overallScore, breakdown, suggestions };
}
